import { useEffect } from 'react';
import { useTheme } from './ThemeProvider';

function setOrCreateMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  if (!content) return;
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function setOrCreateLink(rel: string, href: string) {
  if (!href) return;
  let tag = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

/**
 * Injects SEO metadata (title, description, keywords, og tags, favicon)
 * from branding.meta into the document <head>. Runs whenever branding changes.
 * Mount once near the top of the tree inside ThemeProvider.
 */
const BrandingHead: React.FC = () => {
  const { branding } = useTheme();

  useEffect(() => {
    const meta = branding?.meta;
    const storeName = branding?.store?.name || branding?.name;

    const title = meta?.title || storeName || document.title;
    if (title) document.title = title;

    const description = meta?.description || branding?.store?.description || branding?.description || '';
    setOrCreateMeta('description', description);

    if (meta?.keywords?.length) {
      setOrCreateMeta('keywords', meta.keywords.join(', '));
    }

    // Open Graph (for link previews)
    if (title) setOrCreateMeta('og:title', title, 'property');
    if (description) setOrCreateMeta('og:description', description, 'property');
    if (meta?.ogImage) setOrCreateMeta('og:image', meta.ogImage, 'property');
    setOrCreateMeta('og:type', 'website', 'property');

    // Twitter card
    setOrCreateMeta('twitter:card', 'summary_large_image');
    if (title) setOrCreateMeta('twitter:title', title);
    if (description) setOrCreateMeta('twitter:description', description);
    if (meta?.ogImage) setOrCreateMeta('twitter:image', meta.ogImage);

    // Favicon
    if (meta?.favicon) {
      setOrCreateLink('icon', meta.favicon);
    }

    // Canonical URL (use current URL if not provided)
    const canonical = meta?.canonicalUrl || window.location.origin + window.location.pathname;
    setOrCreateLink('canonical', canonical);
  }, [branding]);

  return null;
};

export default BrandingHead;
